import React, { useState, useCallback, useEffect } from "react";
import { FullscreenIcon } from "@/components/ui/icon";
import { ProgressSlider } from "@/components/common/progress-slider";
import { cn } from "@/utils/cn";
import VolumeControl from "./volume-control";
import IconButton from "./icon-button";
import PlayingButton from "./playing-button";
import PlayButton from "./play-button";
import LoadingButton from "./loading-button";

interface VideoControllerProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  className?: string;
  onLoad?: (load: boolean) => void;
  onError?: (error: string) => void;
}

const VideoController: React.FC<VideoControllerProps> = ({
  videoRef,
  className = "",
  onLoad,
  onError,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [videoVolume, setVideoVolume] = useState(100);
  const [videoMuted, setVideoMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  // 播放/暂停切换
  const togglePlay = useCallback(async () => {
    if (!videoRef.current || hasError) return;

    try {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        await videoRef.current.play();
      }
    } catch (error) {
      console.warn("Video play/pause failed:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Video play/pause failed";
      setHasError(true);
      onError?.(errorMessage);
    }
  }, [isPlaying, videoRef, hasError, onError]);

  // 全屏功能实现
  const toggleFullscreen = useCallback(async () => {
    if (!videoRef.current) return;

    try {
      if (!isFullscreen) {
        // 进入全屏
        if (videoRef.current.requestFullscreen) {
          await videoRef.current.requestFullscreen();
        } else if ((videoRef.current as any).webkitRequestFullscreen) {
          await (videoRef.current as any).webkitRequestFullscreen();
        } else if ((videoRef.current as any).msRequestFullscreen) {
          await (videoRef.current as any).msRequestFullscreen();
        }
      } else {
        // 退出全屏
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          await (document as any).webkitExitFullscreen();
        } else if ((document as any).msExitFullscreen) {
          await (document as any).msExitFullscreen();
        }
      }
    } catch (error) {
      console.warn("Fullscreen toggle failed:", error);
      onError?.("Fullscreen toggle failed");
    }
  }, [isFullscreen, videoRef, onError]);

  // 快进/快退功能
  const seekVideo = useCallback(
    (seconds: number) => {
      if (!videoRef.current || hasError) return;

      const newTime = Math.max(0, Math.min(duration, currentTime + seconds));
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
      if (duration > 0) {
        setProgress((newTime / duration) * 100);
      }
    },
    [currentTime, duration, videoRef, hasError]
  );

  // 进度条变化（提高精度）
  const handleProgressChange = useCallback(
    (value: number) => {
      if (videoRef.current && duration > 0) {
        const newTime = (value / 100) * duration;
        videoRef.current.currentTime = newTime;
        setProgress(value);
        setCurrentTime(newTime);
      }
    },
    [duration, videoRef]
  );

  // 拖拽开始
  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  // 拖拽结束
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // 格式化时间
  const formatTime = useCallback((time: number) => {
    if (!isFinite(time)) return "00:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  }, []);

  // 键盘快捷键支持
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!videoRef.current) return;

      // 防止在输入框中触发
      if (
        (e.target as HTMLElement)?.tagName === "INPUT" ||
        (e.target as HTMLElement)?.tagName === "TEXTAREA"
      ) {
        return;
      }

      switch (e.code) {
        case "Space":
          e.preventDefault();
          togglePlay();
          break;
        case "ArrowLeft":
          e.preventDefault();
          seekVideo(-10); // 快退10秒
          break;
        case "ArrowRight":
          e.preventDefault();
          seekVideo(10); // 快进10秒
          break;
        case "ArrowUp":
          e.preventDefault();
          const newVolumeUp = Math.min(100, videoVolume + 10);
          setVideoVolume(newVolumeUp);
          if (videoRef.current) {
            videoRef.current.volume = newVolumeUp / 100;
          }
          break;
        case "ArrowDown":
          e.preventDefault();
          const newVolumeDown = Math.max(0, videoVolume - 10);
          setVideoVolume(newVolumeDown);
          if (videoRef.current) {
            videoRef.current.volume = newVolumeDown / 100;
          }
          break;
        case "KeyM":
          e.preventDefault();
          setVideoMuted(!videoMuted);
          if (videoRef.current) {
            videoRef.current.muted = !videoMuted;
          }
          break;
        case "KeyF":
          e.preventDefault();
          toggleFullscreen();
          break;
      }
    },
    [togglePlay, seekVideo, videoVolume, videoMuted, toggleFullscreen, videoRef]
  );

  // 监听视频事件
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      if (!isDragging) {
        const current = video.currentTime;
        const total = video.duration || 0;
        setCurrentTime(current);
        setDuration(total);
        if (total > 0) {
          setProgress((current / total) * 100);
        }
      }
    };

    const handleVolumeChange = () => {
      setVideoVolume(Math.round(video.volume * 100));
      setVideoMuted(video.muted);
    };

    const handlePlay = () => {
      setIsPlaying(true);
      setHasError(false);
    };

    const handlePause = () => setIsPlaying(false);

    const handleLoadedData = () => {
      setDuration(video.duration);
      setIsLoading(false);
      setHasError(false);
    };

    const handleLoadStart = () => {
      setIsLoading(true);
      setHasError(false);
    };

    const handleError = () => {
      setIsLoading(false);
      setHasError(true);
      setIsPlaying(false);
      onError?.("Failed to load video");
    };

    const handleWaiting = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("loadeddata", handleLoadedData);
    video.addEventListener("loadstart", handleLoadStart);
    video.addEventListener("error", handleError);
    video.addEventListener("waiting", handleWaiting);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("volumechange", handleVolumeChange);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("loadeddata", handleLoadedData);
      video.removeEventListener("loadstart", handleLoadStart);
      video.removeEventListener("error", handleError);
      video.removeEventListener("waiting", handleWaiting);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("volumechange", handleVolumeChange);
    };
  }, [videoRef, isDragging, onError]);

  // 监听全屏状态变化
  useEffect(() => {
    const handleFullscreenChange = () => {
      // 同步video元素的音量到组件状态
      if (document.fullscreenElement) {
      }
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "msfullscreenchange",
        handleFullscreenChange
      );
    };
  }, []);

  // 添加键盘事件监听
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    onLoad?.(isLoading);
  }, [isLoading]);

  return (
    <div className={cn("flex items-center gap-2 p-2", className)}>
      {/* 播放/暂停按钮 */}
      {isPlaying ? (
        isLoading ? (
          <LoadingButton onClick={togglePlay} disabled={hasError} />
        ) : (
          <PlayingButton onClick={togglePlay} disabled={hasError} />
        )
      ) : (
        <PlayButton onClick={togglePlay} disabled={hasError} />
      )}

      <div className="flex flex-1 items-center gap-2 p-1 h-10 rounded-sm bg-i-normal/60 backdrop-blur-xl">
        {/* 音量控制 */}
        <VolumeControl
          initialVolume={videoVolume}
          initialMuted={videoMuted}
          onVolumeChange={(volume) => {
            if (videoRef.current) {
              setVideoVolume(volume);
              videoRef.current.volume = volume / 100;
            }
          }}
          onMutedChange={(muted) => {
            if (videoRef.current) {
              setVideoMuted(muted);
              videoRef.current.muted = muted;
            }
          }}
        />

        {/* 当前时间 */}
        <span className="text-i-white text-xs font-medium w-auto">
          {formatTime(currentTime)}
        </span>

        {/* 进度条 */}
        <div className="flex-1">
          <ProgressSlider
            value={progress}
            max={100}
            trackColor="rgba(255,255,255,0.5)"
            progressColor="rgb(255,255,255)"
            thumbColor="rgb(255,255,255)"
            thumbBorderColor="rgb(255,255,255)"
            onChange={handleProgressChange}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            disabled={hasError}
            className="w-full"
          />
        </div>

        {/* 总时长 */}
        <span className="text-i-white text-xs font-medium w-auto">
          {formatTime(duration)}
        </span>

        {/* 全屏按钮 */}
        <IconButton onClick={toggleFullscreen} disabled={hasError}>
          <FullscreenIcon className="w-5 h-5" />
        </IconButton>
      </div>
    </div>
  );
};

export default VideoController;
